import { useState, useEffect, useRef, useCallback, useContext, useLayoutEffect } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { Loader2, AlertCircle, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react'
import { useFileIO } from '@papan/files/client/file-io'
import { useRealtime, useOnlineRecovery } from '@papan/realtime/client'
import { IconButton } from './ToolbarControls.js'
import { ViewerToolbarContext } from './viewerToolbar.js'
import { errorText } from '@papan/api-client'

interface ImageViewerProps {
  fileId: string
  fileName?: string
  versionId?: string
  viewSync?: Record<string, Record<string, unknown>>
  viewSyncId?: string
  viewSyncTrigger?: number
}

// Zoom bounds. `null` scale means "fit", whose factor is derived from the box
// and never upscales (matching the plain max-w/max-h look this viewer had).
const MAX_SCALE = 8
const STEP = 1.25
const DOUBLE_TAP_MS = 300
// Fit IS the floor — there is nothing to see in a picture rendered smaller
// than the size at which all of it already shows, and a fixed floor breaks
// down anyway: a picture far larger than the box fits at ~4%, where a 10%
// minimum would turn "zoom out" into a zoom IN.
const clampAt = (v: number, fit: number) => Math.min(MAX_SCALE, Math.max(fit, v))

export function ImageViewer({ fileId, versionId }: ImageViewerProps) {
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshCounter, setRefreshCounter] = useState(0)
  // #1316 review round 2: a load whose answer a session end dropped is asked again through `useDroppedLoadReask` —
  // in the share view it waits until the moved share session lands, never asking as the ended one
  const { reask, begin } = useDroppedLoadReask()
  const { onFileEvent } = useRealtime()
  const recovery = useOnlineRecovery()
  const fileIO = useFileIO()

  // Re-fetch when file is updated (e.g., by AI agent)
  useEffect(() => {
    if (versionId) return // version previews don't need live refresh
    return onFileEvent((event) => {
      if (event.fileId === fileId && (event.type === 'file:updated' || event.type === 'file:restored')) {
        setRefreshCounter(c => c + 1)
      }
    })
  }, [fileId, versionId, onFileEvent])

  useEffect(() => {
    const asked = begin()   // the share session this load goes out as
    // `live` because the request outlives the effect that started it: switching
    // files mid-load let the OLD blob's `.then` run after cleanup and paint the
    // previous file's image under the new file's name.
    let live = true
    // The URL this run created, tracked here so cleanup can revoke it WITHOUT
    // reading state. Revoking inside a `setUrl` updater made the updater impure,
    // which <StrictMode> double-invokes — the rule this file's own sibling
    // (CompactTabs) documents and follows.
    let created: string | null = null
    setLoading(true)
    setError(null)
    fileIO.loadContent(fileId, versionId)
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob)
        if (!live) { URL.revokeObjectURL(objectUrl); return }
        created = objectUrl
        setUrl(objectUrl)
        setLoading(false)
      })
      .catch(err => {
        if (!live) return
        const text = errorText(err, 'Failed to load image')
        // #1316: a dropped answer (its session ended) is asked again as the session now here — never an error
        if (text === null) { asked.dropped(); return }
        setError(text)
        setLoading(false)
      })

    return () => {
      live = false
      if (created) URL.revokeObjectURL(created)
      setUrl(null)
    }
  }, [fileId, versionId, refreshCounter, recovery, fileIO, reask, begin])

  // ── Zoom / pan ──────────────────────────────────────────────────────
  // The zoomed image is laid out at its real pixel size inside a scrolling
  // box, so panning is the browser's own scroll: trackpad, scrollbars and a
  // one-finger swipe all work without us handling them. We only add the
  // gestures the browser has no opinion about — ⌘/Ctrl+wheel, pinch,
  // double-click/tap and drag-to-pan.
  const boxRef = useRef<HTMLDivElement>(null)
  // The scroll box only exists once the image has loaded, so the gesture
  // listeners cannot hang off a plain ref: the effect would run while the
  // viewer was still showing its spinner, find nothing, and never re-run.
  // Holding the node in state re-runs the effect the moment it mounts.
  const [boxEl, setBoxEl] = useState<HTMLDivElement | null>(null)
  const setBox = useCallback((node: HTMLDivElement | null) => {
    boxRef.current = node
    setBoxEl(node)
  }, [])
  const imgRef = useRef<HTMLImageElement>(null)
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null)
  const [scale, setScale] = useState<number | null>(null) // null = fit
  const [fitScale, setFitScale] = useState(1)
  // Gesture handlers are attached once and read the live values from refs —
  // re-subscribing wheel/touch listeners on every zoom step is itself a
  // source of jank on a phone.
  const scaleRef = useRef<number | null>(null)
  const fitRef = useRef(1)
  const clampScale = useCallback((v: number) => clampAt(v, fitRef.current), [])

  // A new file (or a refreshed one) starts fitted again.
  useEffect(() => {
    scaleRef.current = null
    setScale(null)
    setNatural(null)
  }, [fileId, versionId, refreshCounter, reask])

  // What "fit" currently means, so the percentage readout is honest and a
  // zoom step out of fit continues from the size on screen.
  useEffect(() => {
    const box = boxEl
    if (!box || !natural) return
    const measure = () => {
      const w = box.clientWidth - 32 // the wrapper's p-4
      const h = box.clientHeight - 32
      if (w <= 0 || h <= 0) return
      const value = Math.min(1, w / natural.w, h / natural.h)
      fitRef.current = value
      setFitScale(value)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(box)
    return () => ro.disconnect()
  }, [boxEl, natural])

  const effectiveScale = scale ?? fitScale

  // Keep the point under the cursor (or the box centre) still while zooming:
  // remember where it sits inside the image, then re-scroll to it after the
  // new size has been laid out.
  const pendingAnchor = useRef<{ fx: number; fy: number; ax: number; ay: number } | null>(null)
  useLayoutEffect(() => {
    const p = pendingAnchor.current
    pendingAnchor.current = null
    const box = boxRef.current
    const img = imgRef.current
    if (!p || !box || !img) return
    const ir = img.getBoundingClientRect()
    const br = box.getBoundingClientRect()
    box.scrollLeft += (ir.left - br.left + p.fx * ir.width) - p.ax
    box.scrollTop += (ir.top - br.top + p.fy * ir.height) - p.ay
  }, [scale])

  const zoomTo = useCallback((next: number | null, client?: { x: number; y: number }) => {
    const box = boxRef.current
    const img = imgRef.current
    if (box && img) {
      // Measured from what is on screen RIGHT NOW — including a pinch's
      // transform, which is why the commit below reads the anchor first.
      const ir = img.getBoundingClientRect()
      const br = box.getBoundingClientRect()
      const ax = client ? client.x - br.left : box.clientWidth / 2
      const ay = client ? client.y - br.top : box.clientHeight / 2
      const frac = (v: number) => Math.min(1, Math.max(0, v))
      pendingAnchor.current = {
        fx: frac((ax + br.left - ir.left) / (ir.width || 1)),
        fy: frac((ay + br.top - ir.top) / (ir.height || 1)),
        ax,
        ay,
      }
    }
    const value = next === null ? null : clampScale(next)
    scaleRef.current = value
    setScale(value)
  }, [clampScale])

  const zoomBy = useCallback((factor: number, client?: { x: number; y: number }) => {
    zoomTo(clampScale((scaleRef.current ?? fitRef.current) * factor), client)
  }, [zoomTo, clampScale])

  const toggleActualSize = useCallback((client?: { x: number; y: number }) => {
    zoomTo(scaleRef.current === null ? 1 : null, client)
  }, [zoomTo])

  // ⌘/Ctrl + wheel (what a trackpad pinch sends) and a real two-finger pinch.
  // Both need non-passive listeners to stop the browser zooming the page.
  //
  // A pinch does NOT re-lay-out the image on every move: restyling the width
  // makes the browser re-rasterise the full-size bitmap 60×/second, which is
  // what made this crawl on an iPad. Instead the gesture rides a CSS
  // transform (composited, no re-raster) and only the final scale is
  // committed to the layout when the fingers lift.
  useEffect(() => {
    const box = boxEl
    if (!box) return
    const live = () => scaleRef.current ?? fitRef.current
    const spread = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY)
    const midpoint = (t: TouchList) => ({
      x: (t[0].clientX + t[1].clientX) / 2,
      y: (t[0].clientY + t[1].clientY) / 2,
    })

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return // plain wheel = scroll, untouched
      e.preventDefault()
      zoomBy(Math.exp(-e.deltaY / 300), { x: e.clientX, y: e.clientY })
    }

    let pinch: { dist: number; base: number; k: number; mid: { x: number; y: number } } | null = null
    let frame = 0
    let lastTapAt = 0
    // Lifting the second finger after a pinch arrives as a lone touchend —
    // without this it would seed the double-tap timer and the user's next
    // tap would zoom unexpectedly.
    let pinchEndedAt = 0
    const paint = () => {
      frame = 0
      const img = imgRef.current
      if (img && pinch) img.style.transform = `scale(${pinch.k})`
    }
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return
      const img = imgRef.current
      if (!img) return
      const mid = midpoint(e.touches)
      const r = img.getBoundingClientRect()
      img.style.transformOrigin = `${mid.x - r.left}px ${mid.y - r.top}px`
      img.style.willChange = 'transform'
      pinch = { dist: spread(e.touches) || 1, base: live(), k: 1, mid }
    }
    const onTouchMove = (e: TouchEvent) => {
      if (!pinch || e.touches.length !== 2) return
      e.preventDefault()
      pinch.mid = midpoint(e.touches)
      pinch.k = clampScale(pinch.base * (spread(e.touches) / pinch.dist)) / pinch.base
      if (!frame) frame = requestAnimationFrame(paint)
    }
    const onTouchEnd = (e: TouchEvent) => {
      // Double-tap toggles fit ↔ 100% (iOS Safari's dblclick is unreliable).
      if (!pinch && e.touches.length === 0 && e.changedTouches.length === 1) {
        const now = Date.now()
        const t = e.changedTouches[0]
        if (now - pinchEndedAt < DOUBLE_TAP_MS) return
        if (now - lastTapAt < DOUBLE_TAP_MS) {
          lastTapAt = 0
          e.preventDefault()
          toggleActualSize({ x: t.clientX, y: t.clientY })
        } else {
          lastTapAt = now
        }
        return
      }
      if (!pinch || e.touches.length >= 2) return
      const finished = pinch
      pinch = null
      pinchEndedAt = Date.now()
      lastTapAt = 0
      if (frame) { cancelAnimationFrame(frame); frame = 0 }
      const img = imgRef.current
      // Read the anchor from the transformed image, THEN drop the transform:
      // the commit re-lays the image out at the size the fingers ended on.
      zoomTo(clampScale(finished.base * finished.k), finished.mid)
      if (img) {
        img.style.transform = ''
        img.style.willChange = ''
        img.style.transformOrigin = ''
      }
    }

    /** A CANCELLED touch is not a tap.
     *
     *  This used to share `onTouchEnd`, whose double-tap branch asks only "no
     *  pinch, no touches left, one changed touch" — all true of a cancellation.
     *  So a cancel seeded `lastTapAt`, and one genuine tap within 300ms then
     *  toggled the zoom with no double-tap from the user. Easy to hit on iOS,
     *  where the browser cancels a one-finger drag to take over scrolling: pan
     *  a zoomed photo, let go, tap once — and it jumps. */
    const onTouchCancel = (e: TouchEvent) => {
      lastTapAt = 0
      // A pinch in flight still needs committing, or the CSS transform is left
      // applied over a layout that never caught up.
      if (pinch) onTouchEnd(e)
    }

    box.addEventListener('wheel', onWheel, { passive: false })
    box.addEventListener('touchstart', onTouchStart, { passive: true })
    box.addEventListener('touchmove', onTouchMove, { passive: false })
    box.addEventListener('touchend', onTouchEnd, { passive: false })
    box.addEventListener('touchcancel', onTouchCancel, { passive: false })
    return () => {
      if (frame) cancelAnimationFrame(frame)
      box.removeEventListener('wheel', onWheel)
      box.removeEventListener('touchstart', onTouchStart)
      box.removeEventListener('touchmove', onTouchMove)
      box.removeEventListener('touchend', onTouchEnd)
      box.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [boxEl, zoomBy, zoomTo, toggleActualSize, clampScale])

  // ── Where the zoom controls live ───────────────────────────────────────
  // In a shell with a toolbar (MediaEditor) they belong IN it: floated over
  // the picture's corner they went unnoticed — nobody hunts for tools on the
  // canvas. The share viewer mounts this viewer bare, so there the same
  // cluster stays an overlay, pinned top-right where a viewer's controls are
  // conventionally looked for.
  const host = useContext(ViewerToolbarContext)
  const zoomed = scale !== null
  const percent = Math.round(effectiveScale * 100)
  const canZoomOut = effectiveScale > fitScale
  const canZoomIn = effectiveScale < MAX_SCALE
  // Nothing to zoom until the picture is up; an error/spinner with live zoom
  // buttons is a dead control.
  const toolsReady = !loading && !error && !!url

  const zoomControls = (
    <>
      <IconButton
        icon={<ZoomOut size={14} />}
        label="Zoom out"
        disabled={!canZoomOut}
        onClick={() => zoomBy(1 / STEP)}
      />
      <button
        className="px-1.5 text-xs tabular-nums themed-text-secondary hover:themed-text"
        // The label must still SAY the zoom level — it is the only place the
        // number is announced — and must not collide with the "Fit to screen"
        // button beside it, which would leave two identically-named buttons.
        data-tooltip={zoomed ? 'Fit to screen' : 'Actual size (100%)'}
        aria-label={zoomed ? `Zoom ${percent}%, fit to screen` : `Zoom ${percent}%, actual size`}
        onClick={() => toggleActualSize()}
      >
        {percent}%
      </button>
      <IconButton
        icon={<ZoomIn size={14} />}
        label="Zoom in"
        disabled={!canZoomIn}
        onClick={() => zoomBy(STEP)}
      />
      <IconButton
        icon={<Maximize2 size={14} />}
        label="Fit to screen"
        disabled={!zoomed}
        onClick={() => zoomTo(null)}
      />
    </>
  )

  // Published from an effect, never during render: a parent setState while
  // this child renders is a React warning and an extra pass. One action key
  // for the whole cluster — a lone "28%" with no +/- beside it would be
  // useless, so they fold into the overflow menu together.
  useEffect(() => {
    if (!host) return
    host(toolsReady ? [{ key: 'zoom', priority: 1, node: zoomControls }] : [])
    return () => host([])
    // `zoomControls` is rebuilt every render; these are the values it closes
    // over, so the bar re-renders exactly when a button's state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host, toolsReady, percent, zoomed, canZoomIn, canZoomOut, zoomBy, zoomTo, toggleActualSize])

  // Drag-to-pan with the mouse once the image overflows its box. Pointer
  // events on the scrollbars themselves are left to the browser.
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null)
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const box = boxRef.current
    if (!box || e.pointerType === 'touch' || e.button !== 0) return
    const br = box.getBoundingClientRect()
    if (e.clientX > br.left + box.clientWidth || e.clientY > br.top + box.clientHeight) return // scrollbar
    if (box.scrollWidth <= box.clientWidth && box.scrollHeight <= box.clientHeight) return
    drag.current = { x: e.clientX, y: e.clientY, left: box.scrollLeft, top: box.scrollTop }
    box.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const box = boxRef.current
    if (!box || !drag.current) return
    box.scrollLeft = drag.current.left - (e.clientX - drag.current.x)
    box.scrollTop = drag.current.top - (e.clientY - drag.current.y)
  }
  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    drag.current = null
    boxRef.current?.releasePointerCapture(e.pointerId)
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="animate-spin themed-text-muted" size={24} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center">
          <AlertCircle size={32} className="mx-auto mb-2 text-red-400 opacity-60" />
          <p className="text-sm">Failed to load image: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 relative min-h-0 themed-bg-primary">
      <div
        ref={setBox}
        className={`absolute inset-0 ${zoomed ? 'overflow-auto cursor-grab active:cursor-grabbing' : 'overflow-hidden'}`}
        style={{ touchAction: 'pan-x pan-y' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={(e) => toggleActualSize({ x: e.clientX, y: e.clientY })}
      >
        <div className={`flex p-4 ${zoomed ? 'min-w-full min-h-full' : 'w-full h-full'}`}>
          {url && (
            <img
              ref={imgRef}
              src={url}
              alt="Preview"
              draggable={false}
              onLoad={e => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              className="m-auto select-none"
              style={zoomed && natural
                ? { width: natural.w * effectiveScale, height: 'auto', maxWidth: 'none' }
                : { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          )}
        </div>
      </div>

      {!host && (
        // A version preview paints its "v3 · date" badge in this corner, so the
        // cluster drops below it there. `pointer-events-none` on that badge
        // made the clash silent: the buttons kept working, unreadable.
        <div className={`absolute ${versionId ? 'top-14' : 'top-3'} right-3 flex items-center gap-0.5 rounded-full border themed-border themed-bg-secondary px-1 py-0.5 shadow-lg`}>
          {zoomControls}
        </div>
      )}
    </div>
  )
}
